import { useEffect, useRef } from 'react'

const START_ANGLE = -Math.PI / 2
const TWO_PI = Math.PI * 2
const MAX_PARTICLES = 340
const GOLD_BURST_MARKS = [0.02, 0.2, 0.38, 0.56, 0.74, 0.9, 1]

type ParticleKind = 'flame' | 'spark' | 'confetti'

type Particle = {
    x: number
    y: number
    vx: number
    vy: number
    life: number
    maxLife: number
    size: number
    gold: number
    spark: boolean
    kind: ParticleKind
    rotation: number
    spin: number
    width: number
    height: number
    color: [number, number, number]
}

type RingTone = 'pending' | 'failed' | 'succeeded'

type DepositRadialRingProps = {
    progress: number
    goldProgress?: number
    failed?: boolean
    succeeded?: boolean
    active?: boolean
    className?: string
}

const lerp = (a: number, b: number, t: number) => a + (b - a) * t

const clamp01 = (value: number) => Math.min(1, Math.max(0, value))

const mixChannel = (from: number, to: number, t: number) =>
    Math.round(lerp(from, to, t))

const rgba = (r: number, g: number, b: number, a: number) =>
    `rgba(${r}, ${g}, ${b}, ${a})`

const mixRgb = (
    from: [number, number, number],
    to: [number, number, number],
    t: number
): [number, number, number] => [
    mixChannel(from[0], to[0], t),
    mixChannel(from[1], to[1], t),
    mixChannel(from[2], to[2], t)
]

const BLUE_DEEP: [number, number, number] = [56, 90, 201]
const BLUE_MID: [number, number, number] = [122, 168, 255]
const BLUE_CORE: [number, number, number] = [232, 241, 255]
const GOLD_DEEP: [number, number, number] = [196, 140, 18]
const GOLD_MID: [number, number, number] = [245, 197, 24]
const GOLD_CORE: [number, number, number] = [255, 246, 208]
const RED_DEEP: [number, number, number] = [168, 28, 36]
const RED_MID: [number, number, number] = [255, 82, 82]
const RED_CORE: [number, number, number] = [255, 214, 214]
const GREEN_DEEP: [number, number, number] = [16, 110, 56]
const GREEN_MID: [number, number, number] = [46, 196, 112]
const GREEN_CORE: [number, number, number] = [220, 255, 232]
const CONFETTI_COLORS: [number, number, number][] = [
    [245, 197, 24],
    [255, 246, 208],
    [255, 214, 102],
    [255, 255, 255],
    [232, 168, 48],
    [255, 184, 92]
]

const paletteAt = (gold: number, heat: number, tone: RingTone = 'pending') => {
    const deep =
        tone === 'failed'
            ? RED_DEEP
            : tone === 'succeeded'
              ? GREEN_DEEP
              : mixRgb(BLUE_DEEP, GOLD_DEEP, gold)
    const mid =
        tone === 'failed'
            ? RED_MID
            : tone === 'succeeded'
              ? GREEN_MID
              : mixRgb(BLUE_MID, GOLD_MID, gold)
    const core =
        tone === 'failed'
            ? RED_CORE
            : tone === 'succeeded'
              ? GREEN_CORE
              : mixRgb(BLUE_CORE, GOLD_CORE, gold)
    if (heat < 0.55) {
        return mixRgb(deep, mid, heat / 0.55)
    }
    return mixRgb(mid, core, (heat - 0.55) / 0.45)
}

const strokeArc = (
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    radius: number,
    from: number,
    to: number,
    width: number,
    color: string,
    blur = 0
) => {
    if (to - from < 0.001) return
    ctx.save()
    ctx.strokeStyle = color
    ctx.lineWidth = width
    ctx.lineCap = 'round'
    ctx.shadowColor = blur > 0 ? color : 'transparent'
    ctx.shadowBlur = blur
    ctx.beginPath()
    ctx.arc(cx, cy, radius, START_ANGLE + from * TWO_PI, START_ANGLE + to * TWO_PI)
    ctx.stroke()
    ctx.restore()
}

const drawSegmentedArc = (
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    radius: number,
    from: number,
    to: number,
    width: number,
    goldFrom: number,
    goldTo: number,
    tone: RingTone = 'pending'
) => {
    const span = to - from
    if (span < 0.001) return
    const steps = Math.max(18, Math.ceil(span * 96))
    for (let i = 0; i < steps; i += 1) {
        const t0 = i / steps
        const t1 = (i + 1) / steps
        const heat = 0.28 + t1 * 0.72
        const gold = lerp(goldFrom, goldTo, t1)
        const [r, g, b] = paletteAt(gold, heat, tone)
        strokeArc(
            ctx,
            cx,
            cy,
            radius,
            from + span * t0,
            from + span * t1,
            width,
            rgba(r, g, b, 1)
        )
    }
}

const drawTip = (
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    radius: number,
    fill: number,
    gold: number,
    stroke: number,
    tone: RingTone = 'pending'
) => {
    if (fill <= 0.004) return
    const angle = START_ANGLE + fill * TWO_PI
    const tipX = cx + Math.cos(angle) * radius
    const tipY = cy + Math.sin(angle) * radius
    const [cr, cg, cb] = paletteAt(gold, 1, tone)
    const tipGlow = ctx.createRadialGradient(
        tipX,
        tipY,
        0,
        tipX,
        tipY,
        stroke * 3.4
    )
    tipGlow.addColorStop(0, rgba(cr, cg, cb, 0.95))
    tipGlow.addColorStop(0.22, rgba(cr, cg, cb, 0.55))
    tipGlow.addColorStop(1, rgba(cr, cg, cb, 0))
    ctx.save()
    ctx.globalCompositeOperation = 'lighter'
    ctx.fillStyle = tipGlow
    ctx.beginPath()
    ctx.arc(tipX, tipY, stroke * 3.4, 0, TWO_PI)
    ctx.fill()
    ctx.fillStyle = rgba(255, 255, 255, 0.92)
    ctx.beginPath()
    ctx.arc(tipX, tipY, stroke * 0.28, 0, TWO_PI)
    ctx.fill()
    ctx.restore()
    return { tipX, tipY, angle }
}

const toneFromFlags = (failed: boolean, succeeded: boolean): RingTone =>
    failed ? 'failed' : succeeded ? 'succeeded' : 'pending'

export default function DepositRadialRing({
    progress,
    goldProgress = 0,
    failed = false,
    succeeded = false,
    active = true,
    className = 'deposit-success-animation__ring-canvas'
}: DepositRadialRingProps) {
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const progressRef = useRef(progress)
    const goldProgressRef = useRef(goldProgress)
    const toneRef = useRef<RingTone>(toneFromFlags(failed, succeeded))
    const activeRef = useRef(active)
    const particlesRef = useRef<Particle[]>([])
    const lastTsRef = useRef(0)
    const reducedMotionRef = useRef(false)
    const lastGoldBurstRef = useRef(-1)

    progressRef.current = progress
    goldProgressRef.current = goldProgress
    toneRef.current = toneFromFlags(failed, succeeded)
    activeRef.current = active

    useEffect(() => {
        const canvas = canvasRef.current
        if (!canvas) return

        const ctx = canvas.getContext('2d', { alpha: true })
        if (!ctx) return

        const media = window.matchMedia('(prefers-reduced-motion: reduce)')
        const syncMotion = () => {
            reducedMotionRef.current = media.matches
            if (media.matches) {
                particlesRef.current = []
            }
        }
        syncMotion()
        media.addEventListener('change', syncMotion)

        let frameId = 0
        let disposed = false

        const pushParticle = (particle: Particle) => {
            const particles = particlesRef.current
            if (particles.length >= MAX_PARTICLES) {
                particles.shift()
            }
            particles.push(particle)
        }

        const resize = () => {
            const bounds = canvas.getBoundingClientRect()
            const dpr = Math.min(window.devicePixelRatio || 1, 2)
            const width = Math.max(1, Math.round(bounds.width * dpr))
            const height = Math.max(1, Math.round(bounds.height * dpr))
            if (canvas.width !== width || canvas.height !== height) {
                canvas.width = width
                canvas.height = height
            }
        }

        const spawnFlame = (
            cx: number,
            cy: number,
            radius: number,
            fill: number,
            goldMix: number,
            dt: number
        ) => {
            if (reducedMotionRef.current || fill <= 0.002) return
            const angle = START_ANGLE + fill * TWO_PI
            const tipX = cx + Math.cos(angle) * radius
            const tipY = cy + Math.sin(angle) * radius
            const tangentX = -Math.sin(angle)
            const tangentY = Math.cos(angle)
            const count = fill >= 0.999 ? 1 : 3

            for (let i = 0; i < count; i += 1) {
                const drift = (Math.random() - 0.5) * 0.7
                const speed = lerp(38, 92, Math.random()) * (0.7 + dt * 8)
                pushParticle({
                    x: tipX + (Math.random() - 0.5) * 10,
                    y: tipY + (Math.random() - 0.5) * 10,
                    vx: -tangentX * speed + drift * 28,
                    vy: -tangentY * speed + drift * 28,
                    life: 1,
                    maxLife: lerp(0.28, 0.7, Math.random()),
                    size: lerp(3.2, 9.5, Math.random()),
                    gold: goldMix,
                    spark: Math.random() > 0.72,
                    kind: 'flame',
                    rotation: 0,
                    spin: 0,
                    width: 0,
                    height: 0,
                    color: paletteAt(goldMix, 1)
                })
            }
        }

        const spawnFirework = (originX: number, originY: number) => {
            if (reducedMotionRef.current) return
            const count = 28 + Math.floor(Math.random() * 14)
            for (let i = 0; i < count; i += 1) {
                const angle = (i / count) * TWO_PI + Math.random() * 0.2
                const speed = lerp(130, 380, Math.random())
                pushParticle({
                    x: originX,
                    y: originY,
                    vx: Math.cos(angle) * speed,
                    vy: Math.sin(angle) * speed,
                    life: 1,
                    maxLife: lerp(0.6, 1.15, Math.random()),
                    size: lerp(5, 11, Math.random()),
                    gold: 1,
                    spark: true,
                    kind: 'spark',
                    rotation: 0,
                    spin: 0,
                    width: 0,
                    height: 0,
                    color: CONFETTI_COLORS[
                        Math.floor(Math.random() * CONFETTI_COLORS.length)
                    ]
                })
            }
        }

        const spawnConfetti = (originX: number, originY: number, count = 3) => {
            if (reducedMotionRef.current) return
            for (let i = 0; i < count; i += 1) {
                pushParticle({
                    x: originX + (Math.random() - 0.5) * 18,
                    y: originY + (Math.random() - 0.5) * 18,
                    vx: lerp(-90, 90, Math.random()),
                    vy: lerp(-220, -40, Math.random()),
                    life: 1,
                    maxLife: lerp(0.9, 1.6, Math.random()),
                    size: 1,
                    gold: 1,
                    spark: false,
                    kind: 'confetti',
                    rotation: Math.random() * TWO_PI,
                    spin: lerp(-8, 8, Math.random()),
                    width: lerp(5, 11, Math.random()),
                    height: lerp(2.4, 4.6, Math.random()),
                    color: CONFETTI_COLORS[
                        Math.floor(Math.random() * CONFETTI_COLORS.length)
                    ]
                })
            }
        }

        const drawParticles = (ctx2: CanvasRenderingContext2D, dt: number) => {
            const particles = particlesRef.current
            ctx2.save()
            ctx2.globalCompositeOperation = 'lighter'
            for (let i = particles.length - 1; i >= 0; i -= 1) {
                const particle = particles[i]
                particle.life -= dt / particle.maxLife
                if (particle.life <= 0) {
                    particles.splice(i, 1)
                    continue
                }
                if (particle.kind === 'confetti') {
                    particle.vy += 320 * dt
                    particle.vx *= 0.99
                } else {
                    particle.vx *= particle.kind === 'spark' ? 0.94 : 0.96
                    particle.vy *= particle.kind === 'spark' ? 0.94 : 0.96
                    if (particle.kind === 'spark') {
                        particle.vy += 40 * dt
                    }
                }
                particle.x += particle.vx * dt
                particle.y += particle.vy * dt
                particle.rotation += particle.spin * dt
                const fade = particle.life * particle.life
                const [r, g, b] =
                    particle.kind === 'confetti'
                        ? particle.color
                        : paletteAt(
                              particle.gold,
                              particle.spark ? 1 : 0.62,
                              toneRef.current
                          )

                if (particle.kind === 'confetti') {
                    ctx2.save()
                    ctx2.globalCompositeOperation = 'source-over'
                    ctx2.translate(particle.x, particle.y)
                    ctx2.rotate(particle.rotation)
                    ctx2.fillStyle = rgba(r, g, b, 0.88 * particle.life)
                    ctx2.fillRect(
                        -particle.width / 2,
                        -particle.height / 2,
                        particle.width,
                        particle.height
                    )
                    ctx2.restore()
                    continue
                }

                const radius =
                    particle.size *
                    (particle.kind === 'spark' ? 0.8 + fade : 0.45 + fade)
                const glow = ctx2.createRadialGradient(
                    particle.x,
                    particle.y,
                    0,
                    particle.x,
                    particle.y,
                    radius * (particle.spark ? 2.3 : 2.4)
                )
                glow.addColorStop(0, rgba(r, g, b, 0.92 * fade))
                glow.addColorStop(0.35, rgba(r, g, b, 0.42 * fade))
                glow.addColorStop(1, rgba(r, g, b, 0))
                ctx2.fillStyle = glow
                ctx2.beginPath()
                ctx2.ellipse(
                    particle.x,
                    particle.y,
                    radius * (particle.spark ? 0.9 : 1.15),
                    radius * (particle.spark ? 2 : 0.7),
                    Math.atan2(particle.vy, particle.vx),
                    0,
                    TWO_PI
                )
                ctx2.fill()
            }
            ctx2.restore()
        }

        const render = (ts: number) => {
            if (disposed) return
            const last = lastTsRef.current || ts
            const dt = Math.min(0.033, (ts - last) / 1000)
            lastTsRef.current = ts
            resize()

            const width = canvas.width
            const height = canvas.height
            const cx = width / 2
            const cy = height / 2
            const blueRadius = Math.min(width, height) * 0.36
            const stroke = Math.max(14, blueRadius * 0.085)
            const goldRadius = blueRadius + stroke * 0.7
            const blueFill = clamp01(progressRef.current)
            const tone = toneRef.current
            const isFailed = tone === 'failed'
            const isSucceeded = tone === 'succeeded'
            const goldFill = isFailed ? 0 : clamp01(goldProgressRef.current)
            if (isFailed && particlesRef.current.length > 0) {
                particlesRef.current = []
            }

            ctx.clearRect(0, 0, width, height)

            const well = ctx.createRadialGradient(
                cx,
                cy,
                blueRadius * 0.42,
                cx,
                cy,
                goldRadius * 1.18
            )
            well.addColorStop(0, '#000000')
            well.addColorStop(0.46, '#000000')
            well.addColorStop(0.68, 'rgba(0, 0, 0, 0.88)')
            well.addColorStop(0.84, 'rgba(0, 0, 0, 0.28)')
            well.addColorStop(1, 'rgba(0, 0, 0, 0)')
            ctx.fillStyle = well
            ctx.beginPath()
            ctx.arc(cx, cy, goldRadius * 1.18, 0, TWO_PI)
            ctx.fill()

            strokeArc(
                ctx,
                cx,
                cy,
                blueRadius,
                0,
                1,
                stroke,
                'rgba(255, 255, 255, 0.14)'
            )

            if (blueFill > 0.001) {
                strokeArc(
                    ctx,
                    cx,
                    cy,
                    blueRadius,
                    0,
                    blueFill,
                    stroke * 2.1,
                    isFailed
                        ? 'rgba(255, 82, 82, 0.28)'
                        : isSucceeded
                          ? 'rgba(46, 196, 112, 0.28)'
                          : 'rgba(90, 140, 255, 0.22)',
                    28
                )
                drawSegmentedArc(
                    ctx,
                    cx,
                    cy,
                    blueRadius,
                    0,
                    blueFill,
                    stroke,
                    0,
                    0,
                    tone
                )
                strokeArc(
                    ctx,
                    cx,
                    cy,
                    blueRadius,
                    0,
                    blueFill,
                    stroke * 0.38,
                    isFailed
                        ? 'rgba(255, 220, 220, 0.78)'
                        : isSucceeded
                          ? 'rgba(220, 255, 232, 0.78)'
                          : 'rgba(236, 244, 255, 0.72)'
                )
                drawTip(
                    ctx,
                    cx,
                    cy,
                    blueRadius,
                    blueFill,
                    0,
                    stroke,
                    tone
                )
                if (activeRef.current && goldFill < 0.01 && tone === 'pending') {
                    spawnFlame(cx, cy, blueRadius, blueFill, 0, dt)
                }
            }

            if (goldFill > 0.001) {
                strokeArc(
                    ctx,
                    cx,
                    cy,
                    goldRadius,
                    0,
                    1,
                    stroke * 0.9,
                    'rgba(245, 197, 24, 0.12)'
                )
                strokeArc(
                    ctx,
                    cx,
                    cy,
                    goldRadius,
                    0,
                    goldFill,
                    stroke * 2.15,
                    'rgba(245, 197, 24, 0.28)',
                    30
                )
                drawSegmentedArc(
                    ctx,
                    cx,
                    cy,
                    goldRadius,
                    0,
                    goldFill,
                    stroke * 0.92,
                    0.72,
                    1
                )
                strokeArc(
                    ctx,
                    cx,
                    cy,
                    goldRadius,
                    0,
                    goldFill,
                    stroke * 0.34,
                    'rgba(255, 248, 220, 0.8)'
                )
                const tip = drawTip(
                    ctx,
                    cx,
                    cy,
                    goldRadius,
                    goldFill,
                    1,
                    stroke
                )
                if (activeRef.current) {
                    spawnFlame(cx, cy, goldRadius, goldFill, 1, dt)
                    if (goldFill < 0.999) {
                        spawnConfetti(
                            tip?.tipX ?? cx,
                            tip?.tipY ?? cy,
                            Math.random() > 0.45 ? 2 : 1
                        )
                    }

                    const nextBurst = GOLD_BURST_MARKS.find(
                        (mark) =>
                            goldFill >= mark && lastGoldBurstRef.current < mark
                    )
                    if (nextBurst != null) {
                        lastGoldBurstRef.current = nextBurst
                        spawnFirework(
                            tip?.tipX ?? cx,
                            tip?.tipY ?? cy
                        )
                        spawnFirework(
                            cx + (Math.random() - 0.5) * blueRadius,
                            cy + (Math.random() - 0.5) * blueRadius * 0.4
                        )
                        spawnConfetti(cx, cy - blueRadius * 0.2, 10)
                    }
                }
            } else {
                lastGoldBurstRef.current = -1
            }

            drawParticles(ctx, dt)
            frameId = window.requestAnimationFrame(render)
        }

        const observer = new ResizeObserver(resize)
        observer.observe(canvas)
        resize()
        frameId = window.requestAnimationFrame(render)

        return () => {
            disposed = true
            window.cancelAnimationFrame(frameId)
            observer.disconnect()
            media.removeEventListener('change', syncMotion)
        }
    }, [])

    return (
        <canvas
            ref={canvasRef}
            className={className}
            aria-hidden="true"
        />
    )
}
